import { ITicker, Network } from '@kattana-io/common';
import BigNumber from 'bignumber.js';
import useTicker from '../../../hooks/useTicker';
import { ICurrentState } from '../../../reducers/current';

export enum ETabs {
  Swap = 'swap',
  Limit = 'limit',
  SSettings = 'swap_settings',
  LSettings = 'limit_settings',
  RecentTrades = 'recent_trades',
}

export enum EOverlays {
  None = 'none',
  BaseCurrencyMenu = 'base_currency_menu',
  QuoteCurrencyMenu = 'quote_currency_menu',
}

export interface ICoin {
  name: string;
  decimals: number;
  amount: BigNumber;
  address: string;
  symbol?: string;
  balance?: number;
}

interface ITradingState {
  base: ICoin;
  quote: ICoin;
  meta?: ITicker;
  network?: Network;
  tradingTab: ETabs;
  overlay: EOverlays;
  swapSpeed: 'standart' | 'fast' | 'instant';
  slippage: number;
  deadline: number;
  tradesCount: number;
  sendTokens: boolean;
  swapPrice: {
    standart: number;
    fast: number;
    instant: number;
  };
  limitPrice: number;
}

export enum TradingActions {
  Flip = 'TRADING_FLIP',
  SetBaseAmount = 'TRADING_SET_BASE_AMOUNT',
  SetQuoteAmount = 'TRADING_SET_QUOTE_AMOUNT',
  SetBaseCoin = 'TRADING_SET_BASE_COIN',
  SetQuoteCoin = 'TRADING_SET_QUOTE_COIN',
  SetMeta = 'TRADING_SET_META',
  SetNetwork = 'TRADING_SET_NETWORK',
  SetTradingTab = 'TRADING_SET_TRADING_TAB',
  SetOverlay = 'TRADING_SET_OVERLAY',
  SetSwapSpeed = 'TRADING_SET_SWAP_SPEED',
  SetSlippage = 'TRADING_SET_SLIPPAGE',
  SetDeadline = 'TRADING_SET_DEADLINE',
  SetTradesCount = 'TRADING_SET_TRADES_COUNT',
  SetSendTokens = 'TRADING_SET_SEND_TOKENS',
  SetSwapPrice = 'TRADING_SET_SWAP_PRICE',
  SetLimitPrice = 'TRADING_SET_LIMIT_PRICE',
}

export const getInitialTradingState = (
  current: ICurrentState
): ITradingState => {
  const ticker = useTicker(current);
  return {
    base: {
      name: ticker.base || '',
      address: ticker.base_address || '',
      decimals: ticker.base_decimals,
      amount: new BigNumber(0),
    },
    quote: {
      name: ticker.quote || '',
      address: ticker.quote_address || '',
      decimals: ticker.quote_decimals,
      amount: new BigNumber(0),
    },
    meta: current,
    network: Network.fromChainId(1),
    tradingTab: ETabs.Swap,
    overlay: EOverlays.None,
    swapSpeed: 'standart',
    slippage: 0.1,
    deadline: 20,
    tradesCount: 2,
    sendTokens: true,
    swapPrice: { standart: 5, fast: 6, instant: 7 },
    limitPrice: 1300,
  };
};

export const tradingReducer = (
  state: ITradingState,
  action: { payload?: any; type: TradingActions }
): ITradingState => {
  switch (action.type) {
  case TradingActions.Flip:
    return {
      ...state,
      base: {
        ...state.quote,
        amount: BigNumber(0),
      },
      quote: {
        ...state.base,
        amount: BigNumber(0),
      },
    };
  case TradingActions.SetBaseAmount:
    return {
      ...state,
      base: {
        ...state.base,
        amount: new BigNumber(action.payload),
      },
    };
  case TradingActions.SetQuoteAmount:
    return {
      ...state,
      quote: {
        ...state.quote,
        amount: new BigNumber(action.payload),
      },
    };
  case TradingActions.SetBaseCoin:
    return {
      ...state,
      base: action.payload,
    };
  case TradingActions.SetQuoteCoin:
    return {
      ...state,
      quote: action.payload,
    };
  case TradingActions.SetMeta:
    return {
      ...state,
      meta: action.payload,
    };
  case TradingActions.SetNetwork:
    return {
      ...state,
      network: action.payload,
    };
  case TradingActions.SetTradingTab:
    return {
      ...state,
      tradingTab: action.payload,
    };
  case TradingActions.SetOverlay:
    return {
      ...state,
      overlay: action.payload,
    };
  case TradingActions.SetSwapSpeed:
    return {
      ...state,
      swapSpeed: action.payload,
    };
  case TradingActions.SetSlippage:
    return {
      ...state,
      slippage: action.payload,
    };
  case TradingActions.SetDeadline:
    return {
      ...state,
      deadline: action.payload,
    };
  case TradingActions.SetTradesCount:
    return {
      ...state,
      tradesCount: action.payload,
    };
  case TradingActions.SetSendTokens:
    return {
      ...state,
      sendTokens: action.payload,
    };
  case TradingActions.SetSwapPrice:
    return {
      ...state,
      swapPrice: action.payload,
    };
  case TradingActions.SetLimitPrice:
    return {
      ...state,
      limitPrice: action.payload,
    };
  default:
    return state;
  }
};
